import { type ReactNode } from "react";
import { Button, InlineNotice } from "@sfc/ui";
import { AdminLoading } from "./AdminLoading";

type ContentQuery<T> = {
  data: T | undefined;
  isError: boolean;
  refetch: () => Promise<unknown>;
};

/**
 * Initial query state belongs outside loaded content. Cached data stays mounted
 * through refetches and refetch errors, preserving selections and editor drafts.
 * Only use for enabled queries; the caller owns disabled/absent selections.
 * Empty results are successful data and belong to the content component.
 */
export function QueryContent<T>({
  query,
  loadingMessage,
  errorMessage,
  children,
  scope = "page",
}: {
  query: ContentQuery<T>;
  scope?: "page" | "panel";
  loadingMessage: string;
  errorMessage: string;
  children: (data: T) => ReactNode;
}) {
  if (query.data === undefined && !query.isError) {
    return <AdminLoading message={loadingMessage} scope={scope} />;
  }

  return (
    <div className="min-w-0 space-y-6">
      {query.isError && (
        <div className="flex items-center gap-3 py-4" role="alert">
          <InlineNotice tone="error">{errorMessage}</InlineNotice>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void query.refetch()}
          >
            Retry
          </Button>
        </div>
      )}
      {query.data !== undefined && children(query.data)}
    </div>
  );
}
